import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
  UrlSegment,
} from '@angular/router';
import { CustomerService } from '@app/services/customer.service';
import { StorageService } from '@app/services/storage.service';
import { EnvService } from '@app/services/env.service';
import { PermissionAction, PermissionResource } from '@app/core/constants';
import { Observable, of } from 'rxjs';
import { filter, switchMap, take, tap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class AuthGuard {
  contractManagerPaths: string[] = [
    'dashboard',
    'devices',
    'debug-tool',
    'forgot-password',
    'infrastructures',
    'login',
    'select-customer',
    'set-password',
  ];

  constructor(
    private customerService: CustomerService,
    private env: EnvService,
    private router: Router,
    private storage: StorageService
  ) {}

  compareDates() {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    //console.log(route);
    const uvsCredentials = this.storage.get('uvs-credentials');
    const currentEULADate = this.storage.get('eulaDate');

    const segments: string[] = route.url.map((seg: UrlSegment) => seg.path);
    if (segments.includes('DeviceManagement')) {
      return true;
    }
    if (segments.includes('XpertRestApi')) {
      console.log('trying to reach xra');
      return true;
    }

    if (uvsCredentials) {
      const lastEulaDate = new Date('February 21, 2023 03:24:00');
      const formattedDate = new Date(currentEULADate);

      // contract manager mode
      if (this.env.CMMode) {
        if (this.contractManagerPaths.includes(route.url[0].path)) return true;
        this.router.navigate(['/dashboard']);
        return false;
      }

      // super user mode no EULA
      if (uvsCredentials.userObject.IsSuperUser) {
        return true;
      }

      // eula not signed mode
      if (lastEulaDate > formattedDate) {
        this.router.navigate(['/eula']);
        return false;
      }

      if (route.data.subscription || route.data.permissionRequired) {
        if (!route.data.subscription) {
          return this.checkPermission(route.data.permissionRequired);
        }

        return this.customerService.subscriptionsLoaded$.pipe(
          filter((loaded) => loaded),
          take(1),
          switchMap(() => {
            if (!this.customerHasSubscription(route.data.subscription)) {
              this.router.navigate(['/dashboard']);
              return of(false);
            }
            return this.checkPermission(route.data.permissionRequired);
          })
        );
      } else {
        return true;
      }
    } else {
      //console.log('Sending browser to /login');
      this.router.navigate(['/login']);
      return false;
    }
  }

  customerHasSubscription(subscription: string): boolean {
    const subs = this.customerService.subscriptions;
    switch (subscription) {
      case 'AssetTracking':
        return !!subs?.AssetTracking;
      case 'MELT':
        return !!subs?.MELT;
      case 'PatientFlow':
        return !!subs?.PatientFlow;
      case 'SDCT':
        return !!subs?.SDCT;
      case 'StaffSafety':
        return !!subs?.StaffSafety;
      case 'TemperatureMonitoring':
        return !!subs?.TemperatureMonitoring;
      case 'HandHygiene':
        return !!subs?.HandHygiene;
      case 'PeopleFlow':
        return !!subs?.PeopleFlow;
      case 'BedManagement':
        return !!subs?.BedManagement;
      default:
        return true;
    }
  }

  checkPermission(permissionRequired?: PermissionResource): Observable<boolean> {
    if (!permissionRequired) return of(true);
    return this.customerService
      .getPermissionFromAPI(permissionRequired, 'view')
      .pipe(
        tap((allowed) => {
          if (!allowed) this.router.navigate(['/dashboard']);
        })
      );
  }

  getPermission(
    resourceCheck: PermissionResource,
    permission: PermissionAction
  ): boolean {
    //console.log("called from auth guard...")
    //console.log("check for UseCase subscription on the customer object also")
    if (this.customerService.isSuperUser) return true;
    const pO = this.customerService.permissions.find(
      (resource: any) => resource.Name == resourceCheck
    );
    if (pO) {
      switch (permission) {
        case 'view':
          return pO.EnableView;
        case 'edit':
          return pO.EnableEdit;
        case 'add':
          return pO.EnableAdd;
        case 'delete':
          return pO.EnableDelete;
        default:
          return false;
      }
    } else {
      return false;
    }
  }
}
